import { RightBridgeApiService } from '../services/right-bridge-api.service';
import { SessionStorageService } from '../services/session-storage.service';
import { Observable, of, throwError } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { map, catchError } from 'rxjs/operators';
import {
  HttpHeaders,
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { EnvironmentService } from '../services/environment.service';
import { ErrorReportingService } from '../services/error-reporting.service';
import { Content, ContentName } from '../models/content-management-models';
import {
  AuditItem,
  ChangeSummary,
  CheckSolutionsResponse,
  LifeProductsResponse,
  ProfileHistory,
  SolutionLogFilesResponse,
  UnitGridItem,
  UnitListResponse,
} from '../models/admin-models';
import { HeaderResponse } from '../models/grid-models';
import {
  ApiResponse,
  DynamicResponse,
  GridResults,
  NumberData,
  StringResult,
} from '../models/general-models';

@Injectable({ providedIn: 'root' })
export class AdminService {
  private rbs = inject(RightBridgeApiService);
  private ss = inject(SessionStorageService);
  private http = inject(HttpClient);
  private envSvc = inject(EnvironmentService);
  private errSvc = inject(ErrorReportingService);

  private environment;
  unit;
  private org: string;

  constructor() {
    this.environment = this.envSvc.get();
    this.org = this.environment.org;
  }

  setUnit(unit) {
    this.unit = unit;
  }

  searchUnits(search?): Observable<DynamicResponse<'units', UnitGridItem[]>> {
    let url = `${this.rbs.baseUrl}/AppAdmin/SearchUnits?`;
    let paramAdded = false;
    Object.keys(search).forEach(el => {
      switch (el) {
        case 'name':
          if (search.name) {
            url += `${
              paramAdded
                ? `&unitName=${search.name}`
                : `unitName=${search.name}`
            }`;
            paramAdded = true;
          }
          break;
        case 'id':
          if (search.id) {
            url += `${
              paramAdded ? `&unitID=${search.id}` : `unitID=${search.id}`
            }`;
            paramAdded = true;
          }
          break;
        case 'type':
          if (search.type) {
            url += `${
              paramAdded
                ? `&unitType=${search.type}`
                : `unitType=${search.type}`
            }`;
            paramAdded = true;
          }
          break;
        case 'parentUnit':
          if (search.parentUnit) {
            url += `${
              paramAdded
                ? `&parentID=${search.parentUnit}`
                : `parentID=${search.parentUnit}`
            }`;
            paramAdded = true;
          }
          break;
      }
    });

    return this.rbs.getRequest<DynamicResponse<'units', UnitGridItem[]>>(url);
  }

  getUnitList(search?, searchOnID = false): Observable<UnitListResponse> {
    let url = this.rbs.baseUrl + '/Hierarchy/Unit?searchString=';

    if (searchOnID) {
      url = this.rbs.baseUrl + '/Hierarchy/UnitList?searchString=';
    }

    if (search) {
      url += search;
    } else {
      url += '%25';
    }

    return this.rbs.getRequest(url);
  }

  saveUnit(unit, name, type, parent): Observable<{ note: string }> {
    const parentId = parent ? parent : '';

    return this.rbs.postRequest<{ note: string }>(
      `${this.rbs.baseUrl}/AppAdmin/Unit?unitID=${unit}&unitName=${name}&unitType=${type}&parentID=${parentId}`
    );
  }

  saveUnitRole(role, unit, note = null): Observable<void> {
    if (note) {
      return this.rbs.postRequest<void>(
        `${this.rbs.baseUrl}/AppAdmin/Unit/Role?unitID=${unit}&role=${role}&note=${note}`
      );
    } else {
      return this.rbs.postRequest<void>(
        `${this.rbs.baseUrl}/AppAdmin/Unit/Role?unitID=${unit}&role=${role}`
      );
    }
  }

  removeUnitRole(role, unit, note = null): Observable<void> {
    if (note) {
      return this.rbs.deleteRequest<void>(
        `${this.rbs.baseUrl}/AppAdmin/Unit/Role?unitID=${unit}&role=${role}&note=${note}`
      );
    } else {
      return this.rbs.deleteRequest<void>(
        `${this.rbs.baseUrl}/AppAdmin/Unit/Role?unitID=${unit}&role=${role}`
      );
    }
  }

  getUnitDetails(
    unit: string
  ): Observable<DynamicResponse<'unit', UnitGridItem>> {
    return this.rbs.getRequest<DynamicResponse<'unit', UnitGridItem>>(
      `${this.rbs.baseUrl}/AppAdmin/Unit?unitID=${unit}`
    );
  }

  getUnitProfile(
    unit: string,
    app: string,
    ui?: string
  ): Observable<ApiResponse<{ questionnaire: any }>> {
    this.rbs.setApp(app);
    this.setUnit(unit);
    const uiType = ui ? ui : `${app}-unit`;
    return this.rbs.getRequest<ApiResponse<{ questionnaire: any }>>(
      `${this.rbs.baseUrl}/AppAdmin/UnitQuestionnaire?unitID=${unit}&type=${uiType}`
    );
  }

  getUnitProfileReport(
    unit: string,
    app: string,
    type: string
  ): Observable<Blob> {
    this.rbs.setApp(app);
    return this.rbs.postRequestForFile(
      `${this.rbs.baseUrl}/Reports/UnitProfile/${unit}?reportType=${type}`
    );
  }

  getUnitActiveUsersReport(
    unit: string,
    type: string,
    includeInactive: boolean,
    roles?: string
  ): Observable<Blob | null> {
    let url = `${this.rbs.baseUrl}/Reports/UnitActiveUsers/${unit}?reportType=${type}`;
    if (roles) {
      url += `&roles=${roles}`;
    }
    url += `&includeInactive=${includeInactive}`;
    const sessId = this.ss.get('session');
    const headers = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org)
      .set('Accept', `application/${type}`);
    if (type === 'csv') {
      return this.http
        .post(
          url,
          { responseType: type },
          { headers: headers, observe: 'response', responseType: 'blob' }
        )
        .pipe(
          map(res => {
            return res.body;
          }),
          catchError((error: any) => {
            return this.errSvc.handleError(error);
          })
        );
    }
    return this.http
      .post(
        url,
        { responseType: type },
        { headers: headers, observe: 'response', responseType: 'blob' }
      )
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getReportTypes(app: string): Observable<{ reportTypes: string[] }> {
    this.rbs.setApp(app);
    return this.rbs.getRequest<{ reportTypes: string[] }>(
      `${this.rbs.baseUrl}/Reports/UnitReportTypes`
    );
  }

  getGridData(
    gridId,
    unit,
    solution,
    filter?
  ): Observable<ApiResponse<GridResults>> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('responseType', 'application/json');
    let url = `${this.rbs.baseUrl}/UnitProfile/Grid?unitID=${unit}&gridID=${gridId}`;

    if (filter) {
      url += `&filterKey=${filter}`;
    }

    return this.rbs.getRequest<ApiResponse<GridResults>>(url, reqHeaders);
  }

  saveGridData(gridId, unit, app, data): Observable<void> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org);
    return this.rbs.patchRequest<void>(
      `${this.rbs.baseUrl}/UnitProfile/Grid?unitID=${unit}&gridID=${gridId}`,
      data,
      reqHeaders
    );
  }

  getCheckSolutions(): Observable<CheckSolutionsResponse> {
    return this.rbs.getRequest<CheckSolutionsResponse>(
      `${this.rbs.baseUrl}/Admin/KBVersions`
    );
  }

  getProfileHistory(profile, solution): Observable<ApiResponse<ChangeSummary>> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('responseType', 'application/csv');
    return this.rbs.getRequest<ApiResponse<ChangeSummary>>(
      `${this.rbs.baseUrl}/Profile/ChangeHistoryConsolidated/${profile}`,
      reqHeaders
    );
  }

  getBridgeHistory(profile, solution): Observable<ProfileHistory> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('responseType', 'application/csv');
    return this.rbs.getRequest<ProfileHistory>(
      `${this.rbs.baseUrl}/Widget/BridgeHistory/${profile}`,
      reqHeaders
    );
  }

  getProductChangeHistory(
    data,
    solution
  ): Observable<{ productName: string; rows: any[] }> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('responseType', 'application/csv');
    return this.rbs.postRequest<{ productName: string; rows: any[] }>(
      `${this.rbs.baseUrl}/Widget/ProductChangeID`,
      data,
      reqHeaders
    );
  }

  getSimpleChangeHistory(data, solution): Observable<{ rows: any[] }> {
    const { profileId, startDate, endDate } = data;
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('responseType', 'application/csv');
    return this.rbs.getRequest<{ rows: any[] }>(
      `${this.rbs.baseUrl}/Profile/ChangeHistorySimple?profileId=${profileId}&startDate=${startDate}&endDate=${endDate}`,
      reqHeaders
    );
  }

  getUnitProfileHistory(unitId, solution): Observable<ApiResponse<any[]>> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('responseType', 'application/json');
    return this.rbs.getRequest<ApiResponse<any[]>>(
      `${this.rbs.baseUrl}/UnitProfile/History/${unitId}`,
      reqHeaders
    );
  }

  rollbackUnitProfile(id, solution): Observable<void> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('responseType', 'application/json');
    return this.rbs.patchRequest<void>(
      `${this.rbs.baseUrl}/UnitProfile/Rollback/${id}`,
      null,
      reqHeaders
    );
  }

  downloadHistoricUnitProfile(id, solution, type): Observable<Blob | null> {
    const sessId = this.ss.get('session');
    let reqHeaders;

    if (type === 'json') {
      reqHeaders = new HttpHeaders()
        .set('X-RB-S', sessId)
        .set('X-RB-I', solution)
        .set('X-RB-O', this.org)
        .set('accept', 'application/json');
    } else {
      reqHeaders = new HttpHeaders()
        .set('X-RB-S', sessId)
        .set('X-RB-I', solution)
        .set('X-RB-O', this.org)
        .set('accept', 'application/xml');
    }

    const url = `${this.rbs.baseUrl}/UnitProfile/HistoryProfile/${id}`;

    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  downloadUnitProfile(app, unit): Observable<Blob | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/xml')
      .set('Accept', 'application/xml');
    const url = `${this.rbs.baseUrl}/UnitProfile/${unit}?extended=false`;

    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  postKBPackage(
    data: FormData,
    note: string,
    solution: string
  ): Observable<string | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org);

    const url = `${this.rbs.baseUrl}/Admin/SaveKB?note=${note}`;

    return this.http
      .post(url, data, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  postUIConfig(
    data: FormData,
    note: string,
    solution: string
  ): Observable<string | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org);

    const url = `${this.rbs.baseUrl}/Admin/SaveUIConfig?note=${note}`;

    return this.http
      .post(url, data, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  postUnitProfile(
    data: FormData,
    UnitId: string,
    solution: string,
    note?: string
  ): Observable<string | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org);

    let url = `${this.rbs.baseUrl}/Admin/UnitProfile?UnitId=${UnitId}`;

    if (note) {
      url += `&note=${note}`;
    }

    return this.http
      .post(url, data, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  postMassUnitProfile(
    data: FormData,
    solution: string,
    note?: string
  ): Observable<string | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org);

    let url = `${this.rbs.baseUrl}/Admin/BulkUnitProfile`;

    if (note) {
      url += `?note=${note}`;
    }

    return this.http
      .post(url, data, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  postUserUpload(data: FormData): Observable<string> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'pp')
      .set('X-RB-O', this.org);

    const url = `${this.rbs.baseUrl}/User/UploadUsers`;

    return this.http
      .post(url, data, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  downloadXMLProfile(id, solution): Observable<Blob | null> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('accept', 'application/xml');

    const url = `${this.rbs.baseUrl}/Profile/XML/${id}`;

    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  downloadJSONProfile(id, solution): Observable<Blob | null> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('accept', 'application/json');

    const url = `${this.rbs.baseUrl}/Profile/Json/${id}`;

    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  downloadAllUnitProfiles(
    solution,
    extended,
    excelFile
  ): Observable<Blob | null> {
    let url;
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('accept', 'application/zip');

    if (excelFile) {
      url = `${this.rbs.baseUrl}/UnitProfile/AllUnitSettings`;
    } else {
      url = `${this.rbs.baseUrl}/UnitProfile/AllUnits?extended=${extended}`;
    }

    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getSolutionLogFiles(): Observable<SolutionLogFilesResponse | null> {
    const session = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    const url = `${this.rbs.baseUrl}/Admin/SolutionLogFiles`;

    return this.http
      .get<SolutionLogFilesResponse>(url, {
        headers: reqHeaders,
        observe: 'response',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return of(error);
        })
      );
  }

  downloadRecentSLF(solutionID): Observable<Blob | null> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org)
      .set('accept', 'text/plain');

    const url = `${this.rbs.baseUrl}/Admin/SolutionLogFile?solutionName=${solutionID}`;

    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getMappingFile(solutionID = ''): Observable<Blob | null> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', solutionID)
      .set('X-RB-O', this.org)
      .set('accept', 'application/zip');

    const url = `${this.rbs.baseUrl}/Admin/MappingFiles`;

    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  postMappingFile(data: FormData): Observable<object | null> {
    const session = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', 'dl')
      .set('X-RB-O', this.org)
      .set('Accept', 'application/zip');

    const url = `${this.rbs.baseUrl}/Admin/MappingFiles`;
    return this.http
      .post(url, data, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'json',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  deleteLifeProduct(data, column, file): Observable<LifeProductsResponse> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', data.app)
      .set('X-RB-O', this.org);

    let url = `${this.rbs.baseUrl}/LifeProducts/DeleteFromSpreadsheet?table=${data.value}`;

    if (column) {
      url = url + `&matchColumns=${column}`;
    }

    return this.http
      .post(url, file, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return of(error);
        })
      );
  }

  exportLifeProducts(data): Observable<Blob | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', data.app)
      .set('X-RB-O', this.org)
      .set('accept', 'application/csv');

    const url = `${this.rbs.baseUrl}/LifeProducts/ExportSpreadsheet?table=${data.value}`;

    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  importLifeProducts(table, data): Observable<LifeProductsResponse | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', table.app)
      .set('X-RB-O', this.org);

    const url = `${this.rbs.baseUrl}/LifeProducts/ImportSpreadsheet?table=${table.value}`;

    return this.http
      .post<LifeProductsResponse>(url, data, {
        headers: reqHeaders,
        observe: 'response',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return of(error);
        })
      );
  }

  downloadHandlebarTemplates(): Observable<Blob | null> {
    const url = `${this.rbs.baseUrl}/Dtcc/HandlebarTemplates`;
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org)
      .set('accept', 'application/zip');

    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  uploadeHandlebarTemplate(
    id,
    fileContent
  ): Observable<StringResult | HttpErrorResponse> {
    const session = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    const url = `${this.rbs.baseUrl}/Dtcc/HandlebarTemplates?templateID=${id}`;

    return this.http
      .patch(url, fileContent, { headers: reqHeaders, observe: 'response' })
      .pipe(
        map(res => {
          return res.body as StringResult;
        }),
        catchError((error: any) => {
          return of(error);
        })
      );
  }

  uploadReportTemplate(
    data: string
  ): Observable<DynamicResponse<'data', string>> {
    const session = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    const url = `${this.rbs.baseUrl}/Reports/Test`;

    return this.http
      .post(url, data, { headers: reqHeaders, observe: 'response' })
      .pipe(
        map(res => {
          return res.body as DynamicResponse<'data', string>;
        }),
        catchError((error: any) => {
          return of(error);
        })
      );
  }

  requestReportTemplate(data: string): Observable<string> {
    const session = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    const url = `${this.rbs.baseUrl}/Reports/Test`;

    return this.http
      .post(url, data, {
        headers: reqHeaders,
        responseType: 'text',
        observe: 'response',
      })
      .pipe(
        map(res => {
          return res.body as string;
        }),
        catchError((error: any) => {
          return of(error);
        })
      );
  }

  customReport(data): Observable<Blob | null> {
    return this.rbs.getCustomReport(data.payload, data.app, false, data.type);
  }

  getAutomatedReportData(app = 'rb'): Observable<
    {
      reportName: string;
      reportDescription: string;
      grid: string;
      Name: string;
      cadenceValue: string;
    }[]
  > {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org);
    return this.rbs.getRequest<
      {
        reportName: string;
        reportDescription: string;
        grid: string;
        Name: string;
        cadenceValue: string;
      }[]
    >(`${this.rbs.baseUrl}/Reports/AutomatedReport`, reqHeaders);
  }

  postAutomatedReportData(app, data): Observable<void> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org);
    const url = `${this.rbs.baseUrl}/Reports/AutomatedReport`;
    return this.http
      .post(url, data.payload, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return of(error);
        })
      );
  }

  patchAutomatedReportData(app, data): Observable<void> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org);
    const url = `${this.rbs.baseUrl}/Reports/AutomatedReport`;
    return this.http
      .patch(url, data.payload, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return of(error);
        })
      );
  }

  deleteAutomatedReportData(reqBody = null, app = 'rb'): Observable<void> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org);
    return this.rbs.deleteRequest(
      `${this.rbs.baseUrl}/Reports/AutomatedReport`,
      reqHeaders,
      reqBody
    );
  }

  getUIConfigHeaders(data): Observable<HeaderResponse> {
    const session = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', data.implementation)
      .set('X-RB-O', this.org);

    const url = `${this.rbs.baseUrl}/Admin/UIConfigHeaders?uiconfig=${data.uiconfig}&type=${data.type}`;

    return this.http.get<HeaderResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getKBChanges(): Observable<GridResults> {
    const url = `${this.rbs.baseUrl}/AppAdmin/KBChanges`;
    return this.rbs.getRequest<GridResults>(url);
  }

  getKBSectionChanges(changeID: string): Observable<GridResults> {
    const url = `${this.rbs.baseUrl}/AppAdmin/KBSectionChanges?changeID=${changeID}`;
    return this.rbs.getRequest<GridResults>(url);
  }

  getKBSectionChangeData(
    changeID: string,
    sectionID: string
  ): Observable<ApiResponse<string>> {
    const url = `${this.rbs.baseUrl}/AppAdmin/KBSectionChangeData?changeID=${changeID}&sectionID=${sectionID}`;
    return this.rbs.getRequest<ApiResponse<string>>(url);
  }

  getDataDictionary(reqData): Observable<HttpResponse<Blob>> {
    const { solution, firm, group, uiConfig } = reqData;
    const session = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('accept', 'application/csv');

    const url = `${this.rbs.baseUrl}/Reports/DataDictionary?group=${group}&unitID=${firm}&uiconfig=${uiConfig}`;
    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getGroupSchema(reqData): Observable<HttpResponse<Blob>> {
    const { solution, group, response } = reqData;
    const session = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('accept', `application/${response ? 'csv' : 'json'}`);

    const url = `${this.rbs.baseUrl}/Reports/GroupSchema?group=${group}`;
    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getAuditStatus(reqData): Observable<AuditItem[]> {
    const { solution, startDate, endDate, type, limit } = reqData;
    const session = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org);

    let url = `${this.rbs.baseUrl}/Admin/AuditStatus`;

    if (startDate || endDate || type || limit) {
      const queryParams: string[] = [];

      if (startDate) {
        queryParams.push(`startDate=${startDate}`);
      }
      if (endDate) {
        queryParams.push(`endDate=${endDate}`);
      }
      if (type) {
        queryParams.push(`type=${type}`);
      }
      if (limit) {
        queryParams.push(`limit=${limit}`);
      }

      url += `?${queryParams.join('&')}`;
    }

    return this.http
      .get<AuditItem[]>(url, {
        headers: reqHeaders,
      })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }
  deleteAudit(reqData): Observable<NumberData> {
    const { solution, payload } = reqData;
    const session = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org);

    const url = `${this.rbs.baseUrl}/Admin/DeleteAudit`;

    return this.http
      .post<NumberData>(url, payload, {
        headers: reqHeaders,
      })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return of(error);
        })
      );
  }
  retryAudit(reqData): Observable<NumberData> {
    const { solution, payload } = reqData;
    const session = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org);

    const url = `${this.rbs.baseUrl}/Admin/RetryAudit`;

    return this.http
      .post<NumberData>(url, payload, {
        headers: reqHeaders,
      })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return of(error);
        })
      );
  }

  postUploadContent(data): Observable<void> {
    const url = `${this.rbs.baseUrl}/Admin/UnitContent`;
    return this.rbs.postRequest<any>(url, data);
  }

  getContentHistory(data): Observable<Content[]> {
    let url = `${this.rbs.baseUrl}/Admin/UnitContent/History?unitId=${data.unitId}`;
    if (data.type) url += `&contentType=${data.type}`;
    if (data.start) url += `&start=${data.start}`;
    if (data.end) url += `&end=${data.end}`;
    return this.rbs.getRequest<Content[]>(url);
  }

  getContent(data): Observable<Content> {
    let url = `${this.rbs.baseUrl}/Admin/UnitContent?unitId=${data.unitId}`;
    if (data.type) url += `&type=${data.type}`;
    return this.rbs.getRequest<Content>(url);
  }

  getContentRecord(data): Observable<Content> {
    const url = `${this.rbs.baseUrl}/Admin/UnitContent/HistoryRecord?historyId=${data}`;
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    return this.rbs.getRequest<Content>(url, reqHeaders);
  }

  getContentRecordDownload(data): Observable<Blob | null> {
    const url = `${this.rbs.baseUrl}/Admin/UnitContent/HistoryRecord?historyId=${data}`;
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getContentNames(): Observable<ContentName[]> {
    const url = `${this.rbs.baseUrl}/Admin/UnitContent/Names`;
    return this.rbs.getRequest<ContentName[]>(url);
  }
}
